import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useCallback, useMemo } from 'react'
import {
  useSearchStore,
  DEFAULT_FILTERS,
  type SearchTab,
  type SortOption,
} from '../../stores/searchStore'
import { SearchBar } from '../../features/search/SearchBar'
import { SearchTabs } from '../../features/search/SearchTabs'
import { SearchFilters } from '../../features/search/SearchFilters'
import { FilterChips } from '../../features/search/FilterChips'
import { SearchResults } from '../../features/search/SearchResults'
import { SearchDetailPane } from '../../features/search/SearchDetailPane'
import { useDetailPane } from '../../features/search/hooks/useDetailPane'

/* ───── Search Params Schema ───── */

interface SearchPageParams {
  q?: string
  tab?: SearchTab
  sort?: SortOption
  risk_min?: number
  risk_max?: number
  platform?: string
  country?: string
  date_preset?: string
  date_from?: string
  date_to?: string
  cursor?: string
}

const VALID_TABS: SearchTab[] = ['entities', 'groups', 'messages']
const VALID_SORTS: SortOption[] = ['relevance', 'newest', 'risk_score', 'name']

/* ───── Route Definition ───── */

export const Route = createFileRoute('/_authenticated/search')({
  validateSearch: (search: Record<string, unknown>): SearchPageParams => ({
    q: typeof search.q === 'string' ? search.q : undefined,
    tab: VALID_TABS.includes(search.tab as SearchTab)
      ? (search.tab as SearchTab)
      : undefined,
    sort: VALID_SORTS.includes(search.sort as SortOption)
      ? (search.sort as SortOption)
      : undefined,
    risk_min:
      typeof search.risk_min === 'number' || typeof search.risk_min === 'string'
        ? Number(search.risk_min)
        : undefined,
    risk_max:
      typeof search.risk_max === 'number' || typeof search.risk_max === 'string'
        ? Number(search.risk_max)
        : undefined,
    platform: typeof search.platform === 'string' ? search.platform : undefined,
    country: typeof search.country === 'string' ? search.country : undefined,
    date_preset: typeof search.date_preset === 'string' ? search.date_preset : undefined,
    date_from: typeof search.date_from === 'string' ? search.date_from : undefined,
    date_to: typeof search.date_to === 'string' ? search.date_to : undefined,
    cursor: typeof search.cursor === 'string' ? search.cursor : undefined,
  }),
  component: SearchPage,
})

/* ───── Page Component ───── */

function SearchPage() {
  const searchParams = useSearch({ from: '/_authenticated/search' })
  const navigate = useNavigate()
  const detailPane = useDetailPane()

  const setQuery = useSearchStore((s) => s.setQuery)
  const setActiveTab = useSearchStore((s) => s.setActiveTab)
  const setSort = useSearchStore((s) => s.setSort)
  const setFilters = useSearchStore((s) => s.setFilters)
  const setCursor = useSearchStore((s) => s.setCursor)
  const setHasSearched = useSearchStore((s) => s.setHasSearched)

  const query = useSearchStore((s) => s.query)
  const activeTab = useSearchStore((s) => s.activeTab)
  const sort = useSearchStore((s) => s.sort)
  const cursor = useSearchStore((s) => s.cursor)
  const filters = useSearchStore((s) => s.filters)

  // Hydrate store from URL params on mount
  useEffect(() => {
    if (searchParams.q) {
      setQuery(searchParams.q)
      setHasSearched(true)
    }
    if (searchParams.tab) setActiveTab(searchParams.tab)
    if (searchParams.sort) setSort(searchParams.sort)
    if (searchParams.risk_min !== undefined) setFilters({ riskMin: searchParams.risk_min })
    if (searchParams.risk_max !== undefined) setFilters({ riskMax: searchParams.risk_max })
    if (searchParams.platform) setFilters({ platforms: searchParams.platform.split(',') })
    if (searchParams.country) setFilters({ countries: searchParams.country.split(',') })
    if (searchParams.date_preset) setFilters({ datePreset: searchParams.date_preset })
    if (searchParams.date_from) setFilters({ dateFrom: searchParams.date_from })
    if (searchParams.date_to) setFilters({ dateTo: searchParams.date_to })
    if (searchParams.cursor) setCursor(searchParams.cursor)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced sync of store changes back to URL params.
  // Uses replace: false so browser back/forward navigates between search states.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const syncToUrl = useCallback(() => {
    const newSearch: SearchPageParams = {}

    const currentQuery = useSearchStore.getState().query
    const currentTab = useSearchStore.getState().activeTab
    const currentSort = useSearchStore.getState().sort
    const currentCursor = useSearchStore.getState().cursor
    const currentFilters = useSearchStore.getState().filters

    if (currentQuery) newSearch.q = currentQuery
    if (currentTab !== 'entities') newSearch.tab = currentTab
    if (currentSort !== 'relevance') newSearch.sort = currentSort
    if (currentCursor) newSearch.cursor = currentCursor
    if (currentFilters.riskMin !== DEFAULT_FILTERS.riskMin) newSearch.risk_min = currentFilters.riskMin
    if (currentFilters.riskMax !== DEFAULT_FILTERS.riskMax) newSearch.risk_max = currentFilters.riskMax
    if (currentFilters.platforms.length > 0) newSearch.platform = currentFilters.platforms.join(',')
    if (currentFilters.countries.length > 0) newSearch.country = currentFilters.countries.join(',')
    if (currentFilters.datePreset) newSearch.date_preset = currentFilters.datePreset
    if (currentFilters.dateFrom) newSearch.date_from = currentFilters.dateFrom
    if (currentFilters.dateTo) newSearch.date_to = currentFilters.dateTo

    navigate({
      to: '/search',
      search: newSearch,
      replace: false,
    })
  }, [navigate])

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(syncToUrl, 300)
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, activeTab, sort, cursor, filters, syncToUrl])

  // Handle search submission
  const handleSearch = (q: string) => {
    setQuery(q)
    setHasSearched(true)
  }

  // Get results from store for prev/next navigation
  const results = useSearchStore((s) => s.results)

  // Compute current index in results for prev/next
  const currentIndex = useMemo(() => {
    if (!detailPane.selectedId) return -1
    return results.findIndex((r) => r.id === detailPane.selectedId)
  }, [results, detailPane.selectedId])

  const hasPrev = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < results.length - 1

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      const prev = results[currentIndex - 1]
      if (prev) detailPane.updateSelection(prev.id, prev.type)
    }
  }, [currentIndex, results, detailPane])

  const handleNext = useCallback(() => {
    if (currentIndex >= 0 && currentIndex < results.length - 1) {
      const next = results[currentIndex + 1]
      if (next) detailPane.updateSelection(next.id, next.type)
    }
  }, [currentIndex, results, detailPane])

  // Handle result click -- opens detail pane (E06-S25)
  const handleResultClick = (id: string, type: string) => {
    if (type === 'entity' || type === 'group' || type === 'message') {
      if (detailPane.isOpen && detailPane.selectedId === id) {
        // Clicking same item closes the pane
        detailPane.close()
      } else if (detailPane.isOpen) {
        // Clicking different item updates the pane (no close/reopen animation)
        detailPane.updateSelection(id, type as 'entity' | 'group' | 'message')
      } else {
        detailPane.open(id, type as 'entity' | 'group' | 'message')
      }
    }
  }

  return (
    <div className="flex h-full flex-col" data-testid="search-page">
      {/* Search Bar */}
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-4">
        <SearchBar autoFocus onSearch={handleSearch} className="max-w-3xl mx-auto" />
      </div>

      {/* Tabs */}
      <div className="bg-[var(--color-surface)] px-6">
        <SearchTabs className="max-w-5xl mx-auto" />
      </div>

      {/* Filter chips */}
      <div className="px-6 py-2">
        <FilterChips className="max-w-5xl mx-auto" />
      </div>

      {/* Main content: filters panel + results + detail pane */}
      <div className={`flex flex-1 gap-4 px-6 pb-6 overflow-hidden transition-all duration-200 ${detailPane.isOpen ? 'mr-[400px]' : ''}`}>
        <SearchFilters />
        <div className="flex-1 min-w-0">
          <SearchResults onResultClick={handleResultClick} />
        </div>
      </div>

      {/* Detail pane (E06-S25) */}
      {detailPane.isOpen && detailPane.selectedId && detailPane.selectedType && (
        <SearchDetailPane
          id={detailPane.selectedId}
          type={detailPane.selectedType}
          onClose={detailPane.close}
          onPrev={handlePrev}
          onNext={handleNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />
      )}
    </div>
  )
}
